import { Component, OnDestroy, OnInit } from '@angular/core';
import * as dateHolidays from 'date-holidays';
import { Subscription } from 'rxjs';

import { PlanningItem } from '@custom-types/planning';
import { ProjectProgress } from '@custom-types/project';
import { LightUser } from '@custom-types/user';
import { GlobalDataService } from '@providers/global-data/global-data.service';
import { RequestService } from '@providers/request/request.service';
import { RequestV2Service } from '@providers/request-service-v2/request-v2.service';

import * as Moment from 'moment';
import { extendMoment } from 'moment-range';

Moment.locale('fr');

const moment = extendMoment(Moment);

@Component({
  selector: 'app-schedule',
  templateUrl: './schedule.component.html',
  styleUrls: ['./schedule.component.less']
})
export class ScheduleComponent implements OnInit, OnDestroy {
  projectObserver: Subscription;
  startDate: string = '';
  endDate: string = '';
  expectedEnd: string = '';
  adjustedEnd: string = '';
  progress: ProjectProgress = 'compliant';
  objectKeys = Object.keys;

  planning = {};
  currentProject: any;
  owner: LightUser = null;
  progressNumber: number = 0;
  public apiPlanning: any;
  public holidays:any = [];

  constructor(
    private globalDataService: GlobalDataService,
    private requestService: RequestService,
    private requestV2Service: RequestV2Service
  ) {}

  async ngOnInit() {
    this.projectObserver = this.globalDataService
      .getCurrentProject()
      .subscribe(async (project: any) => {
        if (project == null || this.currentProject) {
          return;
        }

        const hd = new dateHolidays();
        hd.init('FR');
        let holidays = [];

        holidays = holidays.concat(hd.getHolidays(moment().weekYear()));
        holidays = holidays.concat(hd.getHolidays(moment().add('year', 1).weekYear()));
        this.holidays = holidays.map(d => moment(d.start));

        this.currentProject = project;

        if (project._planning) {
          this.progressNumber = 0;
        }

        this.startDate = project.dates.start;
        this.endDate = project.dates.end;
        this.expectedEnd = project.dates.expected_end;
        this.adjustedEnd = project.dates.adjusted_end;
        this.progress = project.progress;
        this.owner = this.globalDataService.user;

        const apiPlanningResults = await this.requestV2Service
          .getPlannings(project.id, false, true);
        const apiPlanning = apiPlanningResults.items[0];

        this.apiPlanning = apiPlanning;
        if (this.apiPlanning && this.apiPlanning._related) {
          apiPlanning._related.items.sort((a, b) => {
            const aDate = new Date(a.start_date).getTime();
            const bDate = new Date(b.start_date).getTime();

            if (aDate < bDate) {
              return -1;
            }
            if (aDate === bDate) {
              return 0;
            }
            return 1;
          });

          apiPlanning._related.items = apiPlanning._related.items.map((item: PlanningItem) => {
              if (item.type !== 'milestone' && item.type !== 'task') {
                return null;
              }
              // Progress
              item.progress = Math.round(item.progress * 100);

              // Dates
              const date = new Date(item.start_date);

              item.end_date = this.calculEndDate(item.start_date, item.duration);

              const month = date.getMonth();
              const year = date.getFullYear();

              // Time state
              if (item.type === 'milestone') {
                if (date.getTime() <= new Date().getTime()) {
                  item.timeState = 'PAST';
                } else {
                  item.timeState = 'FUTURE';
                }
              } else {
                if (new Date().getTime() > item.end_date.getTime()) {
                  item.timeState = 'PAST';
                } else if (new Date().getTime() < date.getTime()) {
                  item.timeState = 'FUTURE';
                } else {
                  item.timeState = 'ONGOING';
                  item.leftDays = Math.round(
                    Math.abs((new Date().getTime() - item.end_date.getTime())
                      / (24 * 60 * 60 * 1000))
                  );
                }
              }

              // IsCompleted
              if (item.type === 'milestone') {
                item.isCompleted = item.timeState === 'PAST';
              } else {
                item.isCompleted = item.progress === 100;
              }

              const dateB = moment(new Date(year, month, 0, 0, 0, 0, 0))
                .add('1', 'month').toISOString();
              if (this.planning[dateB]) {
                item.isFirst = false;
                const date = moment(new Date(year, month, 0, 0, 0, 0, 0))
                  .add('1', 'month').toISOString();
                this.planning[date].push(item);
              } else {
                item.isFirst = true;
                const date = moment(new Date(year, month, 0, 0, 0, 0, 0))
                  .add('1', 'month').toISOString();
                this.planning[date] = [item];
              }

              return item;
            }).filter(i => i);
        }
      });
  }

  private calculEndDate(start, duration): any {
    let workedDays = 1;
    const curDate = moment(start);
    while (workedDays < duration) {
      if (
        curDate.isoWeekday() < 6
        && !this.holidays.find(hd => hd.isSame(curDate))
      ) {
        workedDays = workedDays + 1;
      }
      curDate.add(1, 'day');
    }
    return curDate.toDate();
  }

  ngOnDestroy() {
    this.projectObserver.unsubscribe();
  }
}
